import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toastError, toastSuccess } from '@/lib/toast';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import CodeBlock from '@/components/layout/CodeBlock';
import Tabs from '@/components/layout/Tabs';
import StatusDot from '@/components/layout/StatusDot';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiContentCopy, mdiRefresh, mdiLoading } from '@mdi/js';
import { extractApiError } from '@/lib/apiError';
import { getInstallBaseUrl } from '@/lib/tenant';
import type { AgenteDetalheDTO } from '@webip/shared/src/dto/agente';

type Platform = 'linux' | 'windows';

function copyToClipboard(text: string, onDone: () => void) {
    if (!text) return;
    navigator.clipboard.writeText(text).then(onDone);
}

/**
 * Campo somente-leitura pra um dos dois segredos do agente — copiar +
 * regenerar. Veio da ficha da appliance (InstalarAgentes), mas agora o
 * regenerar bate na rota por-agente: a versão por-appliance trocava o token
 * de uma linha só e derrubava a autenticação das appliances irmãs.
 */
function TokenField({
    label,
    value,
    hint,
    onRegenerate,
}: {
    label: string;
    value: string | null | undefined;
    hint: string;
    onRegenerate: () => Promise<void>;
}) {
    const [copied, setCopied] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    return (
        <CampoTexto
            label={label}
            value={value ?? ''}
            onChange={() => {/* somente-leitura — gerado pelo backend */ }}
            readOnly
            mono
            hint={hint}
            append={
                <>
                    {value && (
                        <Botao
                            variant={copied ? 'none-success' : 'none-info'}
                            onClick={() => copyToClipboard(value, () => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}
                            icon={mdiContentCopy}
                        />
                    )}
                    <Botao
                        variant="none-warning"
                        disabled={regenerating}
                        icon={regenerating ? mdiLoading : mdiRefresh}
                        onClick={async () => {
                            if (!window.confirm('Gerar um novo token? Se o agente estiver offline, ele vai precisar ser reconfigurado com o valor novo.')) return;
                            setRegenerating(true);
                            try {
                                await onRegenerate();
                            } finally {
                                setRegenerating(false);
                            }
                        }}
                    />
                </>
            }
        />
    );
}

const PlatformToggle = ({ platform, onChange }: { platform: Platform; onChange: (p: Platform) => void }) => (
    <HBox style={{ gap: '8px' }}>
        <Botao variant={platform === 'linux' ? 'primary' : 'outline-secondary'} onClick={() => onChange('linux')}>
            Linux / macOS
        </Botao>
        <Botao variant={platform === 'windows' ? 'primary' : 'outline-secondary'} onClick={() => onChange('windows')}>
            Windows
        </Botao>
    </HBox>
);

type Props = {
    agentId: string | null;
    onClose: () => void;
    /** Abre o gerador de instalador (enrollment) já apontado pra este agente. */
    onGerarInstalador: (agentId: string) => void;
    /** Algo mudou no agente (host/token) — a listagem recarrega. */
    onAlterado?: () => void;
};

/**
 * Ficha do agente: o que antes era o accordion "Agente" + "Instalar Agentes"
 * da appliance. Mudou de lugar porque os dados são do adapter, não da linha
 * de appliance — com N appliances sob o mesmo agent_id, editar por lá obrigava
 * a escolher uma appliance arbitrária pra representar o agente inteiro.
 */
const AgenteModal = ({ agentId, onClose, onGerarInstalador, onAlterado }: Props) => {
    const [platform, setPlatform] = useState<Platform>('linux');
    const [agentHost, setAgentHost] = useState('');
    const [radiusSecret, setRadiusSecret] = useState('');
    const [copiedRadius, setCopiedRadius] = useState(false);

    const query = useQuery({
        queryKey: ['agente-detalhe', agentId],
        enabled: !!agentId,
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: AgenteDetalheDTO }>(`/api/agente/${agentId}`, { withCredentials: true });
            return res.data.result;
        },
    });

    const agente = query.data;

    useEffect(() => {
        setAgentHost(agente?.agent_host ?? '');
        setRadiusSecret(agente?.radius_secret ?? '');
    }, [agente?.agent_id, agente?.agent_host, agente?.radius_secret]);

    const mutSalvar = useMutation({
        mutationFn: async () => axios.patch(
            `/api/agente/${agentId}`,
            { agent_host: agentHost, radius_secret: radiusSecret },
            { withCredentials: true },
        ),
        onSuccess: (res) => {
            const afetadas = (res.data as { appliances_afetadas?: number })?.appliances_afetadas ?? 0;
            query.refetch();
            onAlterado?.();
            toastSuccess(afetadas > 1
                ? `Agente atualizado nas ${afetadas} appliances que ele atende.`
                : 'Agente atualizado.');
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao salvar o agente');
            toastError(message);
        },
    });

    const sujo = !!agente && (agentHost !== (agente.agent_host ?? '') || radiusSecret !== (agente.radius_secret ?? ''));

    const regenerar = async (tipo: 'agent-token' | 'host-agent-token') => {
        try {
            const res = await axios.post<{ entregue_ao_agente?: boolean }>(
                `/api/agente/${agentId}/${tipo}/regenerate`, {}, { withCredentials: true },
            );
            await query.refetch();
            onAlterado?.();
            toastSuccess(res.data.entregue_ao_agente
                ? 'Token novo gerado e entregue ao agente conectado.'
                : 'Token novo gerado. O agente está offline — reconfigure-o com este valor.');
        } catch (error) {
            const { message } = extractApiError(error, 'Erro ao regenerar o token');
            toastError(message);
        }
    };

    // Instalador estático: o mesmo script pra todo mundo, e o token na linha
    // é o que diz ao backend qual agente/vendor/site é este. Chamado por
    // `irm | iex`, o PowerShell não recebe parâmetros — daí o token ir por
    // variável de ambiente lá, e por flag no sh.
    const installBase = getInstallBaseUrl();
    const montarComando = (script: string, token: string | null | undefined) => {
        const valor = token || '<token>';
        return platform === 'linux'
            ? `curl -fsSL ${installBase}/${script}.sh | sudo sh -s -- --token=${valor}`
            : `$env:WEBIP_TOKEN="${valor}"; irm ${installBase}/${script}.ps1 | iex`;
    };
    const comandoHostAgent = montarComando('install-hostagent', agente?.host_agent_token);

    const abaRoteador = (
        <VBox style={{ gap: '16px', paddingTop: '12px' }}>
            <AlertBox variant="info">
                <VBox style={{ gap: '8px', alignItems: 'flex-start' }}>
                    <span>
                        O caminho recomendado é o instalador pronto: ele já sai com endereço, identidade e credencial
                        embutidos, sem "config wizard" nem token colado à mão.
                    </span>
                    <Botao variant="primary" size="sm" onClick={() => agentId && onGerarInstalador(agentId)}>
                        Gerar instalador para este agente
                    </Botao>
                </VBox>
            </AlertBox>

            <TokenField
                label="Token do Agente"
                value={agente?.agent_token}
                hint="Autentica a conexão do agente com o backend, e é o token do comando acima. É o mesmo valor para todas as appliances deste agente — regenerar aqui atualiza todas de uma vez."
                onRegenerate={() => regenerar('agent-token')}
            />
            <CampoTexto
                label="ID do Agente"
                value={agente?.agent_id ?? ''}
                onChange={() => { }}
                readOnly
                mono
                hint="Não é segredo — o instalador descobre este ID sozinho, a partir do token."
            />
        </VBox>
    );

    const abaHostAgent = (
        <VBox style={{ gap: '16px', paddingTop: '12px' }}>
            <AlertBox variant="info">
                <div>
                    O <strong>agente de host</strong> roda em cada <strong>estação</strong> da rede (não no roteador) e
                    reporta métricas e patches de SO. É opcional — instale só nas máquinas que quiser monitorar.
                </div>
            </AlertBox>

            <PlatformToggle platform={platform} onChange={setPlatform} />
            <CodeBlock value={comandoHostAgent} />

            <TokenField
                label="Token do Host-Agent"
                value={agente?.host_agent_token}
                hint="É o token do comando acima — identifica o site e traz o endereço do adapter pro host-agent."
                onRegenerate={() => regenerar('host-agent-token')}
            />

            <AlertBox variant="success">
                <div>
                    Depois de instalado, a máquina aparece em <strong>Dispositivos → Aguardando Adoção</strong> —
                    é lá que você confirma quais estações fazem parte da rede.
                </div>
            </AlertBox>
        </VBox>
    );

    return (
        <Modal
            show={agentId !== null}
            title={agentId ? `Agente ${agentId}` : 'Agente'}
            onClose={onClose}
            hideControls
            size="lg"
        >
            <VBox style={{ gap: '16px', minWidth: '460px' }}>
                {query.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '24px' }}>
                        <Icon path={mdiLoading} spin size={1.5} />
                    </HBox>
                )}

                {query.isError && <AlertBox variant="danger">Erro ao carregar o agente.</AlertBox>}

                {agente && (
                    <>
                        <HBox style={{ gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <StatusDot
                                status={agente.online ? 'online' : 'offline'}
                                label={agente.online ? `Online · v${agente.version ?? '?'}` : 'Offline'}
                            />
                            <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                                {agente.appliances.length === 0
                                    ? 'Nenhuma appliance vinculada'
                                    : `${agente.appliances.length} appliance${agente.appliances.length > 1 ? 's' : ''}: ${agente.appliances.map((a) => a.nome).join(', ')}`}
                            </span>
                        </HBox>

                        <CampoTexto
                            label="IP do Agente"
                            value={agentHost}
                            onChange={setAgentHost}
                            placeholder="ex: 10.0.0.5"
                            hint="Endereço da máquina onde o adapter roda — usado pela coleta SNMP. Vale para todas as appliances deste agente."
                        />

                        <CampoTexto
                            label="RADIUS Secret"
                            value={radiusSecret}
                            onChange={setRadiusSecret}
                            mono
                            hint="Configurado automaticamente no equipamento via sync. Altere somente se necessário — vale para todas as appliances deste agente."
                            append={
                                radiusSecret && (
                                    <Botao
                                        variant={copiedRadius ? 'none-success' : 'none-info'}
                                        onClick={() => copyToClipboard(radiusSecret, () => { setCopiedRadius(true); setTimeout(() => setCopiedRadius(false), 2000); })}
                                        icon={mdiContentCopy}
                                    />
                                )
                            }
                        />

                        <HBox style={{ justifyContent: 'flex-end' }}>
                            <Botao
                                variant="outline-primary"
                                disabled={mutSalvar.isLoading || !sujo || !agente.adotado}
                                onClick={() => mutSalvar.mutate()}
                                title={agente.adotado ? undefined : 'Disponível depois que o agente for vinculado a uma appliance'}
                            >
                                {mutSalvar.isLoading ? <Icon path={mdiLoading} spin size="18px" /> : 'Salvar'}
                            </Botao>
                        </HBox>

                        {!agente.adotado && (
                            <AlertBox variant="warning">
                                Este agente ainda não tem appliance vinculada — os tokens duráveis só nascem na adoção.
                                Use "Adotar" na listagem para cadastrar o equipamento que ele opera.
                            </AlertBox>
                        )}

                        <Tabs
                            size="lg"
                            tabs={[
                                { id: 'adapter', label: 'Roteador', content: abaRoteador },
                                { id: 'hostagent', label: 'Agente de Host', content: abaHostAgent },
                            ]}
                        />
                    </>
                )}
            </VBox>
        </Modal>
    );
};

export default AgenteModal;
